import React from 'react'
import SharedAgoraVideoCallSurface from './SharedAgoraVideoCallSurface'

const AGORA_LOCAL_TOGGLE_STATE = Object.freeze({
  disabling: 2,
  enabling: 3,
})

export default function MentorChamberAgoraVideoSurface({
  rtcProps = null,
  rtmProps = null,
  agoraRtcProps,
  agoraRtmProps,
  callbacks = {},
  styleProps = {},
  fallback = null,
  handleLeaveRoom,
  shell = null,
  layoutId = null,
  onLayoutChange = null,
  railStateId = null,
  onRailStateChange = null,
  showLayoutSelector = null,
  showRailSelector = null,
  onParticipantsUpdated = null,
  onConnectionStateChange = null,
  onRegisterRuntimeCommands = null,
  onScreenShareStateChanged = null,
  onUserJoin = null,
  onUserLeave = null,
  onCameraClose = null,
  onCameraOpen = null,
  onAudioClose = null,
  onAudioOpen = null,
}) {
  const resolvedRtcProps = rtcProps || agoraRtcProps
  const resolvedRtmProps = rtmProps || agoraRtmProps
  const mergedCallbacks = React.useMemo(() => {
    const nextCallbacks = {
      ...callbacks,
    }

    if (!nextCallbacks.EndCall && typeof handleLeaveRoom === 'function') {
      nextCallbacks.EndCall = () => {
        handleLeaveRoom()
      }
    }

    if (
      typeof onParticipantsUpdated === 'function' &&
      onParticipantsUpdated !== shell?.handleParticipantSnapshot
    ) {
      nextCallbacks['participants-updated'] = onParticipantsUpdated
    }

    if (
      typeof onConnectionStateChange === 'function' &&
      onConnectionStateChange !== shell?.handleConnectionStateChange
    ) {
      nextCallbacks['connection-state-change'] = onConnectionStateChange
    }

    if (
      typeof onRegisterRuntimeCommands === 'function' &&
      onRegisterRuntimeCommands !== shell?.registerRuntimeCommands
    ) {
      nextCallbacks['register-runtime-commands'] = onRegisterRuntimeCommands
    }

    if (
      typeof onScreenShareStateChanged === 'function' &&
      onScreenShareStateChanged !== shell?.handleScreenShareStateChange
    ) {
      nextCallbacks['screen-share-state-changed'] = onScreenShareStateChanged
    }

    if (typeof onUserJoin === 'function') {
      nextCallbacks['user-joined'] = onUserJoin
    }

    if (typeof onUserLeave === 'function') {
      nextCallbacks['user-left'] = onUserLeave
    }

    if (typeof onCameraClose === 'function' || typeof onCameraOpen === 'function') {
      nextCallbacks['local-user-mute-video'] = (state) => {
        if (state === AGORA_LOCAL_TOGGLE_STATE.disabling) {
          onCameraClose?.()
          return
        }

        if (state === AGORA_LOCAL_TOGGLE_STATE.enabling) {
          onCameraOpen?.()
        }
      }
    }

    if (typeof onAudioClose === 'function' || typeof onAudioOpen === 'function') {
      nextCallbacks['local-user-mute-audio'] = (state) => {
        if (state === AGORA_LOCAL_TOGGLE_STATE.disabling) {
          onAudioClose?.()
          return
        }

        if (state === AGORA_LOCAL_TOGGLE_STATE.enabling) {
          onAudioOpen?.()
        }
      }
    }

    return nextCallbacks
  }, [
    callbacks,
    handleLeaveRoom,
    onAudioClose,
    onAudioOpen,
    onCameraClose,
    onCameraOpen,
    onConnectionStateChange,
    onParticipantsUpdated,
    onRegisterRuntimeCommands,
    onScreenShareStateChanged,
    onUserJoin,
    onUserLeave,
    shell?.handleConnectionStateChange,
    shell?.handleParticipantSnapshot,
    shell?.handleScreenShareStateChange,
    shell?.registerRuntimeCommands,
  ])

  return (
    <SharedAgoraVideoCallSurface
      shell={shell}
      fallback={fallback}
      rtcProps={resolvedRtcProps}
      rtmProps={resolvedRtmProps}
      callbacks={mergedCallbacks}
      styleProps={styleProps}
      handleLeaveRoom={handleLeaveRoom}
      layoutId={layoutId}
      onLayoutChange={onLayoutChange}
      railStateId={railStateId}
      onRailStateChange={onRailStateChange}
      showLayoutSelector={showLayoutSelector}
      showRailSelector={showRailSelector}
    />
  )
}

MentorChamberAgoraVideoSurface.displayName = 'MentorChamberAgoraVideoSurface'
